import { emailtemplatesApi } from "api";
import { useCategories } from "api/categories";
import { useEmailTriggers } from "api/hooks";
import { AdminResourceDeleteModal } from "components/molecules";
import { routes } from "consts";
import { EMAIL_TEMPLATE_TYPE, EMAIL_TEMPLATE_TYPE_NAMES } from "consts/emailTemplateType";
import { QUERIES_KEYS } from "queries";
import { useState } from "react";
import { HiChevronDown, HiClock, HiTag, HiPlus } from "react-icons/hi2";
import { useNavigate } from "react-router-dom";
import { useInavalidate } from "utils";
import { EmailPageHeader } from "../components/EmailPageHeader";

export const EmailTriggersPage = (): JSX.Element => {
  const [expandedType, setExpandedType] = useState<number | null>(null);
  const { categories, isLoading } = useCategories();
  const invalidate = useInavalidate(QUERIES_KEYS.EMAIL_TRIGGERS());
  const navigate = useNavigate();
  const { triggers } = useEmailTriggers();

  const handleTypeClick = (type: number) => {
    setExpandedType(expandedType === type ? null : type);
  };

  return (
    <div className="">
      <div className="mb-6 flex justify-between">
        <EmailPageHeader>Zdarzenia</EmailPageHeader>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:gap-6 lg:grid-cols-2">
        {TriggersGroups.map((group, index) => (
          <div
            key={index}
            className="rounded-lg border border-gray-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800"
          >
            <div className="border-b border-gray-200 bg-gray-50 px-4 py-3 dark:border-slate-700 dark:bg-slate-900/40 sm:px-6">
              <h3 className="text-lg font-bold text-gray-900 dark:text-slate-100 sm:text-xl">{group.name}</h3>
              <p className="mt-1 text-xs text-gray-600 dark:text-slate-400 sm:text-sm">{group.description}</p>
            </div>
            <div className="p-4 sm:p-6">
              <ul className="space-y-2">
                {group.types.map((type) => {
                  const typeName = Object.entries(EMAIL_TEMPLATE_TYPE).find(([_, value]) => value === type)?.[0];
                  const typeTriggers = triggers.filter((trigger) => trigger.type === type);
                  const triggerCount = typeTriggers.reduce((sum, t) => sum + t.templates.length, 0);
                  const isExpanded = expandedType === type;

                  return (
                    <li
                      key={type}
                      className="rounded-lg border border-gray-200 bg-gray-50 dark:border-slate-700 dark:bg-slate-700/50"
                    >
                      <div className="flex w-full items-center">
                        <button
                          onClick={() => handleTypeClick(type)}
                          className="flex flex-1 items-center justify-between p-3 text-left transition-colors hover:bg-gray-100 dark:hover:bg-slate-700 sm:p-4"
                        >
                          <div className="flex items-center gap-2 sm:gap-3">
                            <span className="text-sm font-medium text-gray-900 dark:text-slate-200 sm:text-base">
                              {typeName
                                ? EMAIL_TEMPLATE_TYPE_NAMES[typeName as keyof typeof EMAIL_TEMPLATE_TYPE_NAMES]
                                : type}
                            </span>
                            <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-800 dark:bg-blue-900/60 dark:text-blue-200">
                              {triggerCount}
                            </span>
                          </div>
                          <HiChevronDown
                            className={`h-5 w-5 text-gray-500 transition-transform dark:text-slate-400 ${isExpanded ? "rotate-180" : ""}`}
                          />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`${routes.EMAILS}${routes.EMAILS_TRIGGERS_ADD}?type=${type}`);
                          }}
                          className="mr-3 rounded-full p-2 text-blue-600 transition-colors hover:bg-blue-100 dark:text-blue-400 dark:hover:bg-slate-600 sm:mr-4"
                          title="Dodaj nowy szablon"
                        >
                          <HiPlus className="h-5 w-5" />
                        </button>
                      </div>

                      {isExpanded && (
                        <div className="border-t border-gray-200 bg-white px-3 py-3 dark:border-slate-700 dark:bg-slate-800 sm:px-4 sm:py-4">
                          {typeTriggers.length > 0 ? (
                            <div className="space-y-3">
                              {typeTriggers.map((trigger) => (
                                <div key={trigger.id}>
                                  {trigger.templates && trigger.templates.length > 0 && (
                                    <div className="space-y-2">
                                      {trigger.templates.map((template) => {
                                        const templateCategories = categories
                                          ? template.categories
                                              .map((catId) => categories.find((c) => c.id === catId)?.name)
                                              .filter(Boolean)
                                          : [];

                                        return (
                                          <div
                                            key={template.id}
                                            className="rounded-lg border border-gray-300 bg-white p-3 transition-all hover:border-blue-400 hover:shadow-sm dark:border-slate-600 dark:bg-slate-700/50 dark:hover:border-blue-500"
                                          >
                                            <button
                                              onClick={() => {
                                                navigate(routes.EMAILS + routes.EMAILS_TRIGGERS_EDIT(template.id));
                                              }}
                                              className="w-full text-left"
                                            >
                                              <div className="mb-2 flex items-center justify-between gap-2">
                                                <span className="text-sm font-medium text-gray-900 dark:text-slate-200">
                                                  {template.name}
                                                </span>
                                                <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900/60 dark:text-blue-200">
                                                  {template.delay === 0
                                                    ? "Natychmiast"
                                                    : template.delay > 23
                                                      ? `+${template.delay / 24} ${template.delay / 24 === 1 ? "dzień" : "dni"}`
                                                      : `+${template.delay} ${template.delay === 1 ? "godzina" : "godzin"}`}
                                                </span>
                                              </div>
                                              {templateCategories.length > 0 && (
                                                <div className="flex flex-wrap items-center gap-2">
                                                  {templateCategories.map((catName, idx) => (
                                                    <span
                                                      key={idx}
                                                      className="inline-flex items-center rounded-full bg-purple-100 px-2.5 py-0.5 text-xs font-medium text-purple-800 dark:bg-purple-900/60 dark:text-purple-200"
                                                    >
                                                      <HiTag className="mr-1 h-3 w-3" />
                                                      {catName}
                                                    </span>
                                                  ))}
                                                </div>
                                              )}
                                            </button>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-center text-sm text-gray-500 dark:text-slate-400">Brak szablonów</p>
                          )}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        ))}
      </div>

      <AdminResourceDeleteModal
        title="Usuń szablon"
        description="Jesteś pewien że chcesz usunąć szablon?"
        onSuccessDelete={() => invalidate()}
        deleteAction={emailtemplatesApi.delete}
      />
    </div>
  );
};

const TriggersGroups = [
  {
    name: "Logowanie i rejestracja",
    description: "Zdarzenia związane z tworzeniem konta, resetowaniem hasła i zaproszeniami do systemu",
    types: [
      EMAIL_TEMPLATE_TYPE.Account_Created,
      EMAIL_TEMPLATE_TYPE.UserResetPassword,
      EMAIL_TEMPLATE_TYPE.User_SystemInvitation,
      EMAIL_TEMPLATE_TYPE.User_SystemInvitation_Reminder,
    ],
  },
  {
    name: "Płatności",
    description: "Zdarzenia dotyczące płatności, rat, przypomnień i opóźnień w płatnościach",
    types: [
      EMAIL_TEMPLATE_TYPE.PaymentSuccessfull,
      EMAIL_TEMPLATE_TYPE.PaymentReminder,
      EMAIL_TEMPLATE_TYPE.PaymentTrancheCancelled,
      EMAIL_TEMPLATE_TYPE.PaymentTranchePostponed,
      EMAIL_TEMPLATE_TYPE.PaymentDelayed,
      EMAIL_TEMPLATE_TYPE.Payment_Refunded,
    ],
  },
  {
    name: "Zgłoszenia",
    description: "Zdarzenia związane z rejestracją na kurs i usuwaniem użytkowników z kursów",
    types: [EMAIL_TEMPLATE_TYPE.CourseRegistration_Created, EMAIL_TEMPLATE_TYPE.UserRemovedFromCourse],
  },
  {
    name: "Kurs",
    description: "Zdarzenia dotyczące zasobów i materiałów kursowych",
    types: [EMAIL_TEMPLATE_TYPE.CourseResource_Added, EMAIL_TEMPLATE_TYPE.CourseEnded],
  },
  {
    name: "Panel trenera",
    description: "Zdarzenia związane z plikami trenerów i prośbami o dostępność",
    types: [
      EMAIL_TEMPLATE_TYPE.TrainerFileUploaded_NotifyAdmin,
      EMAIL_TEMPLATE_TYPE.TrainerFileUploaded_NotifyTrainer,
      EMAIL_TEMPLATE_TYPE.TrainerAvailabilityRequest_Created,
      EMAIL_TEMPLATE_TYPE.TrainerUpcomingLecture_Reminder,
      EMAIL_TEMPLATE_TYPE.Trainer_SystemInvitation,
      EMAIL_TEMPLATE_TYPE.Trainer_ResetPassword,
    ],
  },
] as const;
